import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Target, Shield, Globe, Heart } from 'lucide-react';

const AboutPage = () => {
  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description: 'Amazon Filtration (K) Ltd was established with a vision to provide reliable filtration solutions'
    },
    {
      year: '2021',
      title: 'First Major Contract',
      description: 'Secured our first major automotive industry contract, establishing our market presence'
    },
    {
      year: '2022',
      title: 'ISO Certification',
      description: 'Achieved ISO 9001:2015 certification for quality management systems'
    },
    {
      year: '2023',
      title: 'Expansion',
      description: 'Expanded operations to serve construction and industrial sectors across Kenya'
    },
    {
      year: '2024',
      title: 'Future Growth',
      description: 'Planned expansion into East African markets and new product development'
    }
  ];

  const values = [
    {
      icon: <Shield className="w-12 h-12 text-primary-600" />,
      title: 'Quality',
      description: 'We maintain the highest standards of quality in all our products and services, ensuring reliability and performance.'
    },
    {
      icon: <Users className="w-12 h-12 text-primary-600" />,
      title: 'Customer Focus',
      description: 'Our customers are at the heart of everything we do. We build lasting relationships through exceptional service.'
    },
    {
      icon: <Target className="w-12 h-12 text-primary-600" />,
      title: 'Innovation',
      description: 'We continuously innovate to provide cutting-edge filtration solutions that meet evolving industry needs.'
    },
    {
      icon: <Globe className="w-12 h-12 text-primary-600" />,
      title: 'Sustainability',
      description: 'We are committed to environmental responsibility and sustainable business practices.'
    },
    {
      icon: <Heart className="w-12 h-12 text-primary-600" />,
      title: 'Integrity',
      description: 'We conduct business with honesty, transparency, and ethical practices in all our interactions.'
    }
  ];

  const team = [
    {
      name: 'Michael Kimaiyo',
      position: 'Managing Director',
      image: '/images/team/michael-kimaiyo.jpg',
      initials: 'MK',
      description: 'Over 15 years experience in filtration industry'
    },
    {
      name: 'Josphat Leteipa',
      position: 'General Manager',
      image: '/images/team/josphat- leteipa.jpg',
      initials: 'JL',
      description: 'Expert in operations management and business development'
    },
    {
      name: 'Harrison Maina',
      position: 'Sales Manager',
      image: '/images/team/harrison-maina.jpg',
      initials: 'HM',
      description: 'Specializes in sales strategy and customer relations'
    }
  ];

  const certifications = [
    { name: 'ISO 9001:2015', description: 'Quality Management System' },
    { name: 'ISO 14001:2015', description: 'Environmental Management' },
    { name: 'OHSAS 18001', description: 'Occupational Health & Safety' },
    { name: 'IATF 16949', description: 'Automotive Quality Management' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              About Amazon Filtration
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted partner in filtration solutions, serving industries across Kenya and beyond
            </p>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 max-w-2xl mx-auto lg:mx-0">
                <p>
                  Founded in 2020, Amazon Filtration (K) Ltd emerged from a vision to provide reliable,
                  high-quality filtration solutions to industries across Kenya. What started as a small
                  operation has grown into a trusted partner for businesses in automotive, construction,
                  agriculture, and industrial sectors.
                </p>
                <p>
                  Our journey began with a simple mission: to protect machines and equipment through
                  superior filtration technology. Today, we serve over 500 clients across various
                  industries, providing everything from standard filters to custom-engineered solutions.
                </p>
                <p>
                  Based in Kenya Industrial Estates, we have built our reputation on quality, reliability,
                  and customer service. Our team of experienced professionals works tirelessly to ensure
                  that every product meets the highest standards and every customer receives exceptional service.
                </p>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img
                src="/about/Amazon-filtration-company-pic.jpg"
                alt="Amazon Filtration manufacturing facility and team"
                className="rounded-lg shadow-lg w-full max-w-xl"
                onError={(e) => {
                  e.currentTarget.src = `https://via.placeholder.com/600x400/1e40af/ffffff?text=Amazon+Filtration`;
                  // eslint-disable-next-line no-console
                  console.log('Failed to load factory image: /about/Amazon-filtration-company-pic.jpg');
                }}
              />
            </div>
          </div>
        </div>
      </div>


      {/* Milestones */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">Key milestones in our company's growth and development</p>
          </div>
          
          {/* Side-by-side timeline on desktop, centered stack on mobile */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {/* Left column: even indices */}
              <div className="space-y-8">
                {milestones.filter((_, i) => i % 2 === 0).map((m, i) => (
                  <div key={`left-${i}`} className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 md:p-7 hover:shadow-md transition-shadow text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-between mb-2">
                      <span className="inline-flex items-center rounded-full bg-primary-50 text-primary-700 px-3 py-1 text-xs font-semibold ring-1 ring-primary-100">
                        {m.year}
                      </span>
                      <span className="hidden md:inline-flex w-8 h-8 items-center justify-center rounded-full bg-primary-600 text-white text-sm font-bold shadow-sm">
                        {i * 2 + 1}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">{m.description}</p>
                  </div>
                ))}
              </div>

              {/* Right column: odd indices */}
              <div className="space-y-8">
                {milestones.filter((_, i) => i % 2 === 1).map((m, i) => (
                  <div key={`right-${i}`} className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 md:p-7 hover:shadow-md transition-shadow text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-between mb-2">
                      <span className="inline-flex items-center rounded-full bg-primary-50 text-primary-700 px-3 py-1 text-xs font-semibold ring-1 ring-primary-100">
                        {m.year}
                      </span>
                      <span className="hidden md:inline-flex w-8 h-8 items-center justify-center rounded-full bg-primary-600 text-white text-sm font-bold shadow-sm">
                        {i * 2 + 2}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">{m.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-4 md:p-6">
                <div className="flex justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Certifications */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Certifications & Standards</h2>
            <p className="text-xl text-gray-600">We maintain the highest standards of quality and compliance</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 text-center hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{cert.name}</h3>
                <p className="text-gray-600 text-sm">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            Discover how our expertise and commitment to quality can benefit your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Get In Touch
            </Link>
            <Link
              to="/vision"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Our Vision
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
